import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, FileText, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCLP = (n) => `$${Math.round(parseFloat(n) || 0).toLocaleString('es-CL').replace(/,/g, '.')}`;
const formatFechaCL = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('es-CL', {
        timeZone: 'America/Santiago',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};
const meses = ['', 'Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function EdpAprobar() {
    const { token } = useParams();
    const [edp, setEdp] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [decision, setDecision] = useState(null); // 'aprobado' | 'rechazado'
    const [comentario, setComentario] = useState('');
    const [nombre, setNombre] = useState('');

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [edpRes, cfgRes] = await Promise.all([
                    axios.get(`${API_URL}/api/public/edp/${token}`),
                    axios.get(`${API_URL}/api/configuracion/public`).catch(() => ({ data: null })),
                ]);
                setEdp(edpRes.data);
                setConfig(cfgRes.data);
                setNombre(edpRes.data.contacto_nombre || '');
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Enlace inválido');
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [token]);

    const handleSubmit = async () => {
        if (!decision) return;
        if (decision === 'rechazado' && !comentario.trim()) {
            toast.error('Para rechazar el EDP, debes incluir un comentario explicando el motivo');
            return;
        }
        try {
            setSubmitting(true);
            const r = await axios.post(`${API_URL}/api/public/edp/${token}/decision`, {
                decision,
                comentario: comentario.trim() || null,
                nombre: nombre.trim() || null,
            });
            setEdp({
                ...edp,
                estado: r.data.estado,
                fecha_respuesta: r.data.fecha_respuesta,
                aprobacion_comentario: comentario.trim(),
                aprobacion_por_nombre: nombre.trim(),
                puede_decidir: false,
            });
            setDecision(null);
            setComentario('');
            toast.success(decision === 'aprobado' ? 'EDP aprobado correctamente' : 'EDP rechazado');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al procesar la decisión');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="animate-spin text-[#3D7A7A]" size={36} />
            </div>
        );
    }

    if (!edp) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-8 text-center">
                        <XCircle className="text-red-500 mx-auto mb-4" size={48} />
                        <h1 className="text-xl font-bold text-slate-900 mb-2">Enlace no válido</h1>
                        <p className="text-sm text-slate-500">El enlace del EDP no existe o ha expirado. Solicita un nuevo enlace al emisor.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const yaResolvido = edp.estado === 'aprobado' || edp.estado === 'rechazado';
    const puedeDecidir = edp.puede_decidir === true;
    const reaperturado = yaResolvido && puedeDecidir; // company reopened the period

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-[#E8F4F4]">
            {/* Top bar with PaySync logo */}
            <div className="bg-white border-b border-slate-200 py-3 px-4 flex items-center justify-center">
                {config?.logo_plataforma_url ? (
                    <img
                        src={`${API_URL}${config.logo_plataforma_url}`}
                        alt={config.nombre_plataforma || 'PaySync'}
                        className="h-9 w-auto object-contain"
                        data-testid="paysync-logo"
                    />
                ) : (
                    <div className="px-4 py-1.5 bg-[#1A2B2B] rounded-lg">
                        <span className="text-white font-bold text-base tracking-wide">{config?.nombre_plataforma || 'PaySync'}</span>
                    </div>
                )}
            </div>

            <div className="py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs text-slate-600 mb-3">
                        <FileText size={14} className="text-[#3D7A7A]" />
                        Aprobación de Estado de Pago
                    </div>
                    <p className="text-xl font-bold text-[#2D5A5A] uppercase tracking-wide" data-testid="empresa-emisora">
                        {edp.empresa_emisora}
                    </p>
                    <h1 className="text-3xl font-bold text-slate-900 mt-2">EDP N° {String(edp.numero_edp).padStart(4, '0')}</h1>
                    <p className="text-slate-500 mt-1">{meses[edp.periodo_mes]} {edp.periodo_anio}</p>
                </div>

                {/* Status banner if already resolved */}
                {yaResolvido && !reaperturado && (
                    <Card className={`mb-4 border-l-4 ${edp.estado === 'aprobado' ? 'border-l-emerald-500 bg-emerald-50' : 'border-l-red-500 bg-red-50'}`}>
                        <CardContent className="p-4 flex items-start gap-3">
                            {edp.estado === 'aprobado' ? (
                                <CheckCircle2 className="text-emerald-600 flex-shrink-0 mt-0.5" size={24} />
                            ) : (
                                <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                            )}
                            <div className="flex-1">
                                <p className={`font-semibold ${edp.estado === 'aprobado' ? 'text-emerald-900' : 'text-red-900'}`}>
                                    Este EDP ya fue {edp.estado} y no se puede modificar
                                </p>
                                <p className="text-xs text-slate-600 mt-1">
                                    {edp.aprobacion_por_nombre ? `Confirmado por ${edp.aprobacion_por_nombre}` : 'Confirmado'}
                                    {edp.fecha_respuesta && (
                                        <> · <Clock size={12} className="inline mr-1" />{formatFechaCL(edp.fecha_respuesta)} (hora Chile)</>
                                    )}
                                </p>
                                {edp.aprobacion_comentario && (
                                    <p className="text-sm text-slate-700 mt-2 italic">"{edp.aprobacion_comentario}"</p>
                                )}
                                <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                                    Si necesitas revisar nuevamente este Estado de Pago, contacta al emisor para que vuelva el período al estado <span className="font-semibold">"En Curso"</span>. Solo entonces podrás aprobar o rechazar de nuevo.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Reopened banner */}
                {reaperturado && (
                    <Card className="mb-4 border-l-4 border-l-amber-500 bg-amber-50">
                        <CardContent className="p-4 flex items-start gap-3">
                            <Clock className="text-amber-600 flex-shrink-0 mt-0.5" size={24} />
                            <div className="flex-1">
                                <p className="font-semibold text-amber-900">EDP reabierto para nueva revisión</p>
                                <p className="text-sm text-amber-800 mt-1">
                                    Este EDP fue {edp.estado} previamente
                                    {edp.aprobacion_por_nombre ? ` por ${edp.aprobacion_por_nombre}` : ''}.
                                    El emisor solicitó volver a revisarlo. Tu nueva decisión reemplazará la anterior.
                                </p>
                                {edp.aprobacion_comentario && (
                                    <p className="text-xs text-slate-600 mt-2 italic">Comentario anterior: "{edp.aprobacion_comentario}"</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* EDP details */}
                <Card className="mb-4">
                    <CardContent className="p-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                            <div>
                                <p className="text-xs text-slate-500 uppercase tracking-wide">Cliente</p>
                                <p className="font-semibold text-slate-900">{edp.cliente_nombre}</p>
                                <p className="text-xs text-slate-500">{edp.cliente_rut}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-500 uppercase tracking-wide">Faena / Sitio</p>
                                <p className="font-semibold text-slate-900">{edp.faena || '—'}</p>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Descripción del servicio</p>
                            <p className="text-slate-900">{edp.descripcion}</p>
                            {edp.comentarios && <p className="text-sm text-slate-500 mt-2 italic">{edp.comentarios}</p>}
                        </div>

                        <div className="bg-slate-50 rounded-lg p-4 space-y-1">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Monto Neto</span>
                                <span className="font-medium">{formatCLP(edp.monto_neto)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-600">IVA (19%)</span>
                                <span className="font-medium">{formatCLP(edp.iva)}</span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-slate-200">
                                <span className="font-bold text-slate-900">Total EDP</span>
                                <span className="font-bold text-[#3D7A7A] text-lg">{formatCLP(edp.monto_total)}</span>
                            </div>
                        </div>

                        {edp.pdf_url && (
                            <a
                                href={`${window.location.origin}${edp.pdf_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-[#3D7A7A] hover:text-[#2D5A5A] font-medium"
                                data-testid="ver-pdf-link"
                            >
                                <FileText size={16} />
                                Ver PDF completo del EDP
                            </a>
                        )}
                    </CardContent>
                </Card>

                {/* Decision form */}
                {puedeDecidir && (
                    <Card>
                        <CardContent className="p-6 space-y-4">
                            <h2 className="font-semibold text-slate-900">Tu decisión</h2>

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setDecision('aprobado')}
                                    data-testid="btn-aprobar"
                                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                                        decision === 'aprobado'
                                            ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                                            : 'border-slate-200 hover:border-emerald-300 bg-white'
                                    }`}
                                >
                                    <CheckCircle2 className={decision === 'aprobado' ? 'text-emerald-600' : 'text-slate-400'} size={28} />
                                    <span className={`font-semibold ${decision === 'aprobado' ? 'text-emerald-900' : 'text-slate-700'}`}>Aprobar</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDecision('rechazado')}
                                    data-testid="btn-rechazar"
                                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                                        decision === 'rechazado'
                                            ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                                            : 'border-slate-200 hover:border-red-300 bg-white'
                                    }`}
                                >
                                    <XCircle className={decision === 'rechazado' ? 'text-red-600' : 'text-slate-400'} size={28} />
                                    <span className={`font-semibold ${decision === 'rechazado' ? 'text-red-900' : 'text-slate-700'}`}>Rechazar</span>
                                </button>
                            </div>

                            {decision && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="nombre">Tu nombre</Label>
                                        <Input
                                            id="nombre"
                                            value={nombre}
                                            onChange={(e) => setNombre(e.target.value)}
                                            placeholder="Ej: Rodolfo Díaz"
                                            data-testid="input-nombre"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="comentario">
                                            Comentario {decision === 'rechazado' && <span className="text-red-500">*</span>}
                                        </Label>
                                        <Textarea
                                            id="comentario"
                                            rows={3}
                                            value={comentario}
                                            onChange={(e) => setComentario(e.target.value)}
                                            placeholder={decision === 'rechazado' ? 'Indica el motivo del rechazo' : 'Comentario opcional'}
                                            data-testid="input-comentario"
                                        />
                                    </div>

                                    <Button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className={`w-full ${decision === 'aprobado' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                                        data-testid="btn-confirmar"
                                    >
                                        {submitting ? (
                                            <Loader2 className="animate-spin" size={18} />
                                        ) : (
                                            `Confirmar ${decision === 'aprobado' ? 'aprobación' : 'rechazo'}`
                                        )}
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}

                <p className="text-center text-xs text-slate-400 mt-6">
                    {config?.nombre_plataforma || 'PaySync'} · Aprobación segura de Estados de Pago
                </p>
            </div>
            </div>
        </div>
    );
}
